import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupData, PopupInnerComponentClass, Service, Timeframe } from 'webapp-types';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormSelectWithPopupAsDropdownComponent from 'webapp/components/form/select-with-popup-as-dropdown';

export interface Args {
	model: { timeframe: Timeframe | null };
	onChange: (value: Timeframe | null) => void;
	isDisabled?: boolean;
}

const FAKE_ID = 'fake_timeframe';

export default abstract class CommonTimeframeDropdownComponent<
	T extends Args,
> extends Component<T> {
	@service('timeframe') declare timeframeService: Service.Timeframe;

	abstract popupComponent: PopupInnerComponentClass;
	abstract popupExtraClass: string;

	showIcon = true;

	@tracked dateLabel = '';
	private dateLabelPromise: Promise<string> | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.updateDateLabel();
	}

	get popupClass() {
		return `popup-type-timeframe ${this.popupExtraClass}`;
	}

	get model() {
		return this.args.model;
	}

	get selectedForDropdown() {
		return FAKE_ID;
	}

	get optionsForDropdown() {
		const timeframe = this.model.timeframe;
		let label = this.timeframeService.getTimeframeString(timeframe, true);
		if (timeframe && this.dateLabel) {
			label += ` (${this.dateLabel})`;
		}
		return [
			{
				value: FAKE_ID,
				label,
			},
		];
	}

	@action
	updateDateLabel() {
		this.dateLabel = '';

		const timeframe = this.model.timeframe;

		if (!timeframe) {
			this.dateLabelPromise = null;
			return;
		}

		const promise = this.timeframeService.getTimeframePeriodsLabel(timeframe);
		void promise.then((label) => {
			if (this.dateLabelPromise === promise) {
				this.dateLabel = label;
				this.dateLabelPromise = null;
			}
		});
		this.dateLabelPromise = promise;
	}

	@action
	getPopupProps() {
		return {
			selected: this.model.timeframe,
			onChange: this.args.onChange,
			disablePast: true,
		} as PopupData.Common.TimeframeContext;
	}

	@action
	doNothing() {
		// changes on this dropdown will go through the popup
		// since the dropdown cannot even be opened
	}
	<template>
		<div class="input-dropdown timeframe" {{didUpdate this.updateDateLabel @model.timeframe}}>
			{{#if this.showIcon}}
				<CommonUiIconComponent @name="CalendarClock" @size="lg" @class="timeframe-icon" />
			{{/if}}
			<FormSelectWithPopupAsDropdownComponent
				@selected={{this.selectedForDropdown}}
				@options={{this.optionsForDropdown}}
				@onChange={{this.doNothing}}
				@isNumeric={{false}}
				@popupOffset={{0}}
				@popupComponent={{this.popupComponent}}
				@popupClass={{this.popupClass}}
				@getPopupProps={{this.getPopupProps}}
				@popupSkipDropdownClasses={{true}}
				@isDisabled={{@isDisabled}}
			/>
		</div>
	</template>
}
